import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import dashboardApiService from '../services/dashboardApiService';
import { useMigrationHelper } from '../utils/migrationHelper';

// Custom hook for dashboard data management
const useDashboardData = (config = {}) => {
  const {
    autoRefresh = false,
    refreshInterval = 600000, // 10 minutes instead of 5
    enableCaching = true,
    cacheExpiry = 120000 // 2 minutes cache expiry
  } = config;

  const { useModernAPI } = useMigrationHelper();
  const refreshIntervalRef = useRef(null);
  const cacheRef = useRef({});

  // Consolidate state for simplicity
  const [statistics, setStatistics] = useState({});
  const [formData, setFormData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [upcomingEvents] = useState([]); // Removed setUpcomingEvents since it's not used
  const [userRole, setUserRole] = useState(0);
  const [daysSinceLastUpdate, setDaysSinceLastUpdate] = useState(0);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cache management is simplified as we fetch all data at once.
  const getCachedData = useCallback((key) => {
    if (!enableCaching) return null;
    
    const cached = cacheRef.current[key];
    if (cached && Date.now() - cached.timestamp < cacheExpiry) {
      return cached.data;
    }
    return null;
  }, [enableCaching, cacheExpiry]);

  const setCachedData = useCallback((key, data) => {
    if (!enableCaching) return;
    
    cacheRef.current[key] = {
      data,
      timestamp: Date.now()
    };
  }, [enableCaching]);

  // Set API mode based on migration helper
  useEffect(() => {
    // This could be deprecated if the old API is no longer in use
    dashboardApiService.setApiMode(useModernAPI);
  }, [useModernAPI]);

  // The old fetch functions (fetchDashboardStats, fetchFormData, etc.) are no longer needed.
  // They are replaced by a single refresh function.

  // Merge patient records with status data
  // This function is no longer needed as the backend handles the merge.
  // const mergePatientRecordsWithStatus = useCallback((records) => {
  //   return dashboardApiService.mergePatientRecordsWithStatus(records, patientStatusMaps);
  // }, [patientStatusMaps]);

  // Fetch medication prescription list for a patient
  const fetchMedpassPrescriptionList = useCallback(async (mrnNumber) => {
    try {
      const result = await dashboardApiService.fetchMedpassPrescriptionList(mrnNumber);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  // Fetch patient record dashboard data
  const fetchPatientRecordDashboard = useCallback(async (fromDate, toDate) => {
    try {
      const result = await dashboardApiService.fetchPatientRecordDashboard(fromDate, toDate);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  // Unified refresh function
  const refreshDashboard = useCallback(async (forceRefresh = false) => {
    const useCache = !forceRefresh;
    const cacheKey = 'dashboardSummary';

    if (useCache) {
      const cached = getCachedData(cacheKey);
      if (cached) {
        setStatistics(cached.statistics);
        setFormData(cached.formData);
        setTotalRows(cached.totalRows);
        setUserRole(cached.userRole);
        setDaysSinceLastUpdate(cached.daysSinceLastUpdate);
        return { success: true, data: cached };
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await dashboardApiService.fetchDashboardSummary();
      
      if (result.success) {
        const { statistics, formData, totalRows, userRole, daysSinceLastUpdate } = result.data;
        
        setStatistics(statistics);
        setFormData(formData);
        setTotalRows(totalRows);
        setUserRole(userRole);
        setDaysSinceLastUpdate(daysSinceLastUpdate);
        
        setCachedData(cacheKey, result.data);
      } else {
        throw new Error(result.error || 'Failed to refresh dashboard');
      }
      
      return result;
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [getCachedData, setCachedData, useModernAPI]);

  // Refresh staff dashboard data (includes patient status)
  const refreshStaffDashboard = useCallback(async (forceRefresh = false) => {
    const useCache = !forceRefresh;
    const cacheKey = 'staffDashboardSummary';

    if (useCache) {
      const cached = getCachedData(cacheKey);
      if (cached) {
        setStatistics(cached.stats);
        setFormData(cached.patients);
        setTotalRows(cached.patients.length);
        // Set other state from cache if needed
        return { success: true, data: cached };
      }
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await dashboardApiService.fetchStaffDashboardSummary();

      if (result.success) {
        const { stats, patients } = result.data;
        setStatistics(stats);
        setFormData(patients);
        setTotalRows(patients.length);
        
        // Assuming userRole and daysSinceLastUpdate might come from another source
        // or could be added to the new endpoint if needed.
        // For now, we fetch them separately if required.
        // await fetchDashboardStats(useCache); // Example if still needed

        setCachedData(cacheKey, result.data);
        return { success: true, data: result.data };
      } else {
        throw new Error(result.error || 'Failed to refresh staff dashboard');
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [getCachedData, setCachedData]);

  // Clear cache
  const clearCache = useCallback(() => {
    cacheRef.current = {};
  }, []);

  // Auto-refresh functionality - FIXED: Removed refreshDashboard from dependencies
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(() => {
        // Call the API directly without dependencies to avoid infinite loop
        dashboardApiService.fetchDashboardSummary().then(result => {
          if (result.success && result.data) {
            setStatistics(result.data.statistics || {});
            setFormData(result.data.formData || []);
            setTotalRows(result.data.totalRows || 0);
            setUserRole(result.data.userRole || 0);
            setDaysSinceLastUpdate(result.data.daysSinceLastUpdate || 0);
          }
        }).catch(error => {
          console.error('Auto-refresh failed:', error);
        });
      }, refreshInterval);
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh, refreshInterval]); // Removed refreshDashboard from dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  return useMemo(() => ({
    // Statistics
    statistics,
    isLoadingStats: isLoading,
    statsError: error,
    
    // Form data
    formData,
    formFields: [], // No longer managed here
    totalRows,
    isLoadingForms: isLoading,
    formsError: error,
    
    // Events
    upcomingEvents,
    isLoadingEvents: isLoading,
    eventsError: error,
    
    // Patient status
    patientStatusMaps: [], // No longer managed here
    isLoadingPatientStatus: isLoading,
    patientStatusError: error,
    
    // User info
    userRole,
    daysSinceLastUpdate,
    
    // General state
    isAnyLoading: isLoading,
    hasAnyError: !!error,
    isLoading,
    error,
    
    // Actions
    refreshDashboard,
    refreshStaffDashboard,
    fetchMedicationDetails: fetchMedpassPrescriptionList, // Alias for clarity
    fetchPatientRecordDashboard,
    clearCache
  }), [
    statistics, formData, totalRows, upcomingEvents, userRole, daysSinceLastUpdate,
    isLoading, error,
    refreshDashboard, refreshStaffDashboard, fetchMedpassPrescriptionList,
    fetchPatientRecordDashboard, clearCache
  ]);
};

export default useDashboardData; 